import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useCallback, useMemo } from 'react';
import { apiTokenRequest } from './msalConfig';
import { RedirectInProgressError, isInteractionInProgress } from './redirectInProgress';

export function useAuth() {
  // #14687 — inProgress guards against issuing a second top-level navigation.
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0] ?? null;

  const login = useCallback(() => {
    instance.loginRedirect({ scopes: ['openid', 'profile'] });
  }, [instance]);

  const logout = useCallback(() => {
    instance.logoutRedirect({ postLogoutRedirectUri: '/' });
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!account) throw new Error('No authenticated account');
    try {
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // #14687 — throw rather than `return ''`: an empty string is indistinguishable from a real
        // token, so the caller fetched with an empty bearer and 401'd instead of simply letting the
        // redirect complete.
        if (isInteractionInProgress(inProgress)) throw new RedirectInProgressError();
        await instance.acquireTokenRedirect(apiTokenRequest);
        throw new RedirectInProgressError();
      }
      throw error;
    }
  }, [instance, account, inProgress]);

  const roles = useMemo<string[]>(() => {
    if (!account?.idTokenClaims) return [];
    const claims = account.idTokenClaims as Record<string, unknown>;
    const r = claims['roles'];
    return Array.isArray(r) ? r : [];
  }, [account]);

  const displayName = account?.name ?? account?.username ?? '';
  const email = account?.username ?? '';
  const entraId = account?.localAccountId ?? '';

  return {
    isAuthenticated,
    account,
    displayName,
    email,
    entraId,
    roles,
    login,
    logout,
    getAccessToken,
  };
}
